// Import Firebase app first
import { app, analytics, auth, db } from './firebase';

// Firestore
import { collection, addDoc, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

// Auth functions
import { 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';

// React & Recharts
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, ResponsiveContainer } from 'recharts';

import './index.css';

// Card Engine Module (unchanged)
const CardEngine = {
  generateDeck() {
    const colors = ['red', 'green', 'purple'];
    const shapes = ['oval', 'diamond', 'squiggle'];
    const numbers = [1, 2, 3];
    const shadings = ['solid', 'striped', 'outline'];
    
    const deck = [];
    for (const color of colors) {
      for (const shape of shapes) {
        for (const number of numbers) {
          for (const shading of shadings) {
            deck.push({ color, shape, number, shading });
          }
        }
      }
    }
    return deck;
  },

  isSet(cards) {
    const attrs = ['color', 'shape', 'number', 'shading'];
    return attrs.every(attr => {
      const vals = cards.map(c => c[attr]);
      const unique = new Set(vals).size;
      return unique === 1 || unique === 3;
    });
  },

  generateTestTriple(forceSet = null) {
    const deck = this.generateDeck();
    const shouldBeSet = forceSet !== null ? forceSet : Math.random() < 0.5;
    
    if (shouldBeSet) {
      let attempts = 0;
      while (attempts < 100) {
        const indices = [];
        while (indices.length < 3) {
          const idx = Math.floor(Math.random() * deck.length);
          if (!indices.includes(idx)) indices.push(idx);
        }
        const cards = indices.map(i => deck[i]);
        if (this.isSet(cards)) {
          return { cards, isSet: true };
        }
        attempts++;
      }
    }
    
    let attempts = 0;
    while (attempts < 100) {
      const indices = [];
      while (indices.length < 3) {
        const idx = Math.floor(Math.random() * deck.length);
        if (!indices.includes(idx)) indices.push(idx);
      }
      const cards = indices.map(i => deck[i]);
      if (!this.isSet(cards)) {
        return { cards, isSet: false };
      }
      attempts++;
    }
    
    return { cards: [deck[0], deck[1], deck[2]], isSet: false };
  }
};

// Card SVG Component (unchanged)
const CardSVG = ({ card }) => {
  const colors = {
    red: '#DD3333',
    green: '#00AA00',
    purple: '#800080'
  };

  const color = colors[card.color];
  const patternId = `pattern-${card.color}-${card.shape}-${card.number}-${Math.random()}`;

  const renderShape = (shape, x, scaleFactor) => {
    const fill = card.shading === 'solid' ? color : 'none';
    const strokeWidth = 9;
    const centerY = 50;
    const maskId = `mask-stripe-${patternId}`; // This maskId is not used correctly below

    // Original paths scaled and translated to position
    if (shape === 'oval') {
      const scale = 0.16 * scaleFactor;
      const pathCenterX = 93.5;
      const pathCenterY = 200;
      const offsetX = x - pathCenterX * scale;
      const offsetY = centerY - pathCenterY * scale;
      
      return (
        <path 
          d="m11.49999,95.866646c0,-44.557076 37.442923,-81.999998 82.000002,-81.999998l12.000015,0c44.557076,0 81.999992,37.442923 81.999992,81.999998l0,206.133354c0,44.557098 -37.442917,82 -81.999992,82l-12.000015,0c-44.557079,0 -82.000002,-37.442902 -82.000002,-82l0,-206.133354z"
          fill={fill}
          stroke={color}
          strokeWidth={strokeWidth}
          transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`}
        />
      );
    } else if (shape === 'diamond') {
      const scale = 0.16 * scaleFactor;
      const pathCenterX = 98.5;
      const pathCenterY = 200;
      const offsetX = x - pathCenterX * scale;
      const offsetY = centerY - pathCenterY * scale;
      
      return (
        <path 
          d="m98.544521,10.311863l-87.830189,189.330815l88.201143,189.644391l88.942329,-190.362741l-89.313283,-188.612465z"
          fill={fill}
          stroke={color}
          strokeWidth={strokeWidth}
          transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`}
        />
      );
    } else { // Squiggle
      const scale = 0.16 * scaleFactor;
      const pathCenterX = 98;
      const pathCenterY = 195;
      const offsetX = x - pathCenterX * scale;
      const offsetY = centerY - pathCenterY * scale;
      
      return (
        <path 
          d="m67.892902,12.746785c43.231313,-6.717223 107.352741,6.609823 121.028973,58.746408c13.676233,52.136585 -44.848649,161.467192 -45.07116,204.650732c4.566246,56.959708 83.805481,87.929227 22.329944,105.806022c-61.475536,17.876795 -126.122496,-1.855045 -143.73294,-41.933823c-17.610444,-40.07878 49.274638,-120.109409 46.14822,-188.091997c-3.126418,-67.982588 -21.873669,-70.257464 -49.613153,-80.177084c-27.739485,-9.919618 5.678801,-52.283035 48.910115,-59.000258z"
          fill={fill}
          stroke={color}
          strokeWidth={strokeWidth}
          transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`}
        />
      );
    }
  };

  let positions;
  const scaleFactor = 1.0;
  
  if (card.number === 1) {
    positions = [75]; // Center
  } else if (card.number === 2) {
    positions = [50, 100]; // Two shapes equally spaced
  } else {
    positions = [35, 75, 115]; // Three shapes equally spaced
  }

  return (
    <svg className="w-full h-auto" viewBox="0 0 150 100">
      <defs>
        <pattern id={patternId} width="14" height="20" patternUnits="userSpaceOnUse">
          <rect width="14" height="20" fill="white" />
          <line x1="0" y1="10" x2="14" y2="10" stroke={color} strokeWidth="4" />
        </pattern>
      </defs>
      {positions.map((x, i) => (
        <g key={i}>
          {card.shading === 'striped' ? (
            <>
              {React.cloneElement(renderShape(card.shape, x, scaleFactor), { 
                fill: `url(#${patternId})`,
                stroke: color,
                strokeWidth: 2 // Adjust stroke width for striped to not be too thick with pattern
              })}
            </>
          ) : (
            renderShape(card.shape, x, scaleFactor)
          )}
        </g>
      ))}
    </svg>
  );
};

// Analytics Module (UPDATED for Firestore)
const Analytics = {
  async saveAttempt(userId, attempt) {
    if (!userId) {
      console.warn('Cannot save attempt: User not authenticated.');
      return false;
    }
    try {
      await addDoc(collection(db, 'attempts'), {
        ...attempt,
        userId: userId,
        timestamp: new Date() // Use Firestore Timestamp
      });
      return true;
    } catch (error) {
      console.error('Error saving attempt to Firestore:', error);
      return false;
    }
  },

  async getAttempts(userId, limitCount = 100) {
    if (!userId) {
      console.warn('Cannot get attempts: User not authenticated.');
      return [];
    }
    try {
      const q = query(
        collection(db, 'attempts'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'), // Order by timestamp descending
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      const attempts = [];
      querySnapshot.forEach((doc) => {
        attempts.push(doc.data());
      });
      // Sort in ascending order for trend charts if needed
      return attempts.reverse(); 
    } catch (error) {
      console.error('Error getting attempts from Firestore:', error);
      return [];
    }
  },

  computeStats(attempts) { // (unchanged logic, only data source changes)
    if (attempts.length === 0) {
      return {
        total: 0,
        correct: 0,
        accuracy: 0,
        avgTime: 0,
        byAttribute: {}
      };
    }

    const correct = attempts.filter(a => a.correct).length;
    const avgTime = attempts.reduce((sum, a) => sum + a.responseTime, 0) / attempts.length;

    const byAttribute = {};
    const attrs = ['color', 'shape', 'number', 'shading'];
    
    for (const attr of attrs) {
      const allSame = attempts.filter(a => {
        const vals = a.cards.map(c => c[attr]);
        return new Set(vals).size === 1;
      });
      const allDiff = attempts.filter(a => {
        const vals = a.cards.map(c => c[attr]);
        return new Set(vals).size === 3;
      });

      byAttribute[attr] = {
        allSame: {
          total: allSame.length,
          correct: allSame.filter(a => a.correct).length,
          accuracy: allSame.length > 0 ? parseFloat((allSame.filter(a => a.correct).length / allSame.length * 100).toFixed(1)) : 0
        },
        allDiff: {
          total: allDiff.length,
          correct: allDiff.filter(a => a.correct).length,
          accuracy: allDiff.length > 0 ? parseFloat((allDiff.filter(a => a.correct).length / allDiff.length * 100).toFixed(1)) : 0
        }
      };
    }

    return {
      total: attempts.length,
      correct,
      accuracy: (correct / attempts.length * 100).toFixed(1),
      avgTime: avgTime.toFixed(2),
      byAttribute
    };
  }
};

// Main App Component (UPDATED for Auth and Firestore)
export default function SetTrainer() {
  const [gameState, setGameState] = useState('welcome');
  const [currentTriple, setCurrentTriple] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [stats, setStats] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [pendingTimeout, setPendingTimeout] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
      if (currentUser) {
        loadStats(currentUser.uid);
      } else {
        setStats(null); // Clear stats if no user
        setAttempts([]);
      }
    });
    return () => unsubscribe(); // Cleanup subscription
  }, []);

  const loadStats = async (uid) => {
    if (!uid) return;
    const allAttempts = await Analytics.getAttempts(uid);
    setAttempts(allAttempts);
    const computed = Analytics.computeStats(allAttempts);
    setStats(computed);
  };

  const startGame = () => {
    if (!user) {
      alert('Please sign in to start training!');
      setGameState('welcome');
      return;
    }
    const triple = CardEngine.generateTestTriple();
    setCurrentTriple(triple);
    setStartTime(Date.now());
    setGameState('playing');
  };

  const handleAnswer = async (userAnswer) => {
    if (!currentTriple || !startTime || !user) return;

    const responseTime = (Date.now() - startTime) / 1000;
    const correct = userAnswer === currentTriple.isSet;

    const attempt = {
      cards: currentTriple.cards,
      isSetCorrect: currentTriple.isSet,
      userAnswer,
      correct,
      responseTime,
      // timestamp field is added in Analytics.saveAttempt
    };

    // Save to Firestore
    const saved = await Analytics.saveAttempt(user.uid, attempt);
    if (!saved) console.warn('Attempt was not saved!');
    
    setFeedback(correct ? 'Correct! 🎉' : 'Incorrect 😕');
    setGameState('feedback');

    const timeout = setTimeout(() => {
      loadStats(user.uid);
      startGame();
      setFeedback(null);
      setPendingTimeout(null);
    }, 100);
    
    setPendingTimeout(timeout);
  };

  const viewStats = () => {
    if (!user) {
      alert('Please sign in to view your analytics!');
      return;
    }
    setGameState('stats');
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setAuthError('');
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setAuthError(error.message);
    }
  };

  const handleEmailSignUp = async () => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      setAuthError('');
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Email Sign-Up Error:', error);
      setAuthError(error.message);
    }
  };

  const handleEmailSignIn = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setAuthError('');
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Email Sign-In Error:', error);
      setAuthError(error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setAuthError('');
    } catch (error) {
      console.error('Sign-Out Error:', error);
      setAuthError(error.message);
    }
  };

  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center p-4">
        <div className="text-2xl font-semibold text-purple-700">Loading user...</div>
      </div>
    );
  }

  if (gameState === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <h1 className="text-4xl font-bold text-center mb-2 text-purple-600">Set Trainer</h1>
          <p className="text-gray-600 text-center mb-6">Master the card game Set</p>

          {!user ? (
            <div className="space-y-4 mb-6">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {authError && <p className="text-red-500 text-sm text-center">{authError}</p>}
              <button
                onClick={handleEmailSignIn}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition"
              >
                Sign In with Email
              </button>
              <button
                onClick={handleEmailSignUp}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-indigo-700 transition"
              >
                Sign Up with Email
              </button>
              <div className="flex items-center justify-between my-2">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink mx-4 text-gray-500">OR</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>
              <button
                onClick={handleGoogleSignIn}
                className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-red-700 transition flex items-center justify-center"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="w-5 h-5 mr-2" />
                Sign In with Google
              </button>
            </div>
          ) : (
            <div className="text-center mb-6">
              <p className="text-lg text-gray-700 mb-2">Welcome, {user.displayName || user.email}!</p>
              <button
                onClick={handleSignOut}
                className="bg-gray-400 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-500 transition"
              >
                Sign Out
              </button>
            </div>
          )}
          
          <div className="space-y-4">
            <button
              onClick={startGame}
              className={`w-full py-4 rounded-lg font-semibold text-lg transition ${user ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              disabled={!user}
            >
              Start Training
            </button>
            
            <button
              onClick={viewStats}
              className={`w-full py-4 rounded-lg font-semibold text-lg transition ${user ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              disabled={!user}
            >
              View Analytics
            </button>
          </div>

          {user && stats && stats.total > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-center mb-2">Your Stats</h3>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <div className="font-bold text-purple-600">{stats.total}</div>
                  <div className="text-gray-600">Attempts</div>
                </div>
                <div>
                  <div className="font-bold text-green-600">{stats.accuracy}%</div>
                  <div className="text-gray-600">Accuracy</div>
                </div>
                <div>
                  <div className="font-bold text-blue-600">{stats.avgTime}s</div>
                  <div className="text-gray-600">Avg Time</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (gameState === 'stats' && stats && user) {
    const attrData = Object.keys(stats.byAttribute).map(attr => ({
      name: attr.charAt(0).toUpperCase() + attr.slice(1),
      'All Same': stats.byAttribute[attr].allSame.accuracy,
      'All Different': stats.byAttribute[attr].allDiff.accuracy
    }));

    const recentAttempts = attempts.slice(-20).map((a, i) => ({
      attempt: i + 1,
      time: a.responseTime.toFixed(2)
    }));

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 p-4">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-3xl font-bold mb-6 text-purple-600">Your Analytics</h2>
          <p className="text-gray-600 mb-4">Signed in as: {user.displayName || user.email}</p>
          
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.total}</div>
              <div className="text-gray-600">Total Attempts</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600">{stats.accuracy}%</div>
              <div className="text-gray-600">Accuracy</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.avgTime}s</div>
              <div className="text-gray-600">Avg Response Time</div>
            </div>
          </div>

          <h3 className="text-xl font-semibold mb-4">Accuracy by Attribute</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={attrData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="All Same" fill="#8b5cf6" />
              <Bar dataKey="All Different" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>

          {recentAttempts.length > 0 && (
            <>
              <h3 className="text-xl font-semibold mb-4 mt-8">Response Time Trend (Last 20)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={recentAttempts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="attempt" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="time" stroke="#8b5cf6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}

          <button
            onClick={() => setGameState('welcome')}
            className="w-full mt-8 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  if (!currentTriple || !user) return null; // Should not happen if game start is guarded

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 flex flex-col items-center p-2 sm:p-4">
      {/* Header */}
      <div className="w-full flex justify-between items-center mb-1 sm:mb-2 max-w-4xl">
        <h2 className="text-xl sm:text-2xl font-bold text-purple-600">Is this a Set?</h2>
        {stats && (
          <div className="text-xs sm:text-sm text-gray-600">
            Score: {stats.correct}/{stats.total} ({stats.accuracy}%)
          </div>
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`text-center text-sm sm:text-lg font-bold mb-1 sm:mb-2 ${
          feedback.includes('Correct') ? 'text-green-600' : 'text-red-600'
        }`}>
          {feedback}
        </div>
      )}

      {/* Cards container */}
      <div className="flex justify-center gap-2 sm:gap-4 flex-wrap w-full max-w-4xl mb-2 sm:mb-4">
        {currentTriple.cards.map((card, i) => (
          <div
            key={i}
            className="bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center"
            style={{
              width: '28vw',        // responsive width for small screens
              maxWidth: '140px',    // cap width on desktop
              minWidth: '90px',     // avoid too tiny cards
              height: '18vw',       // height proportional
              maxHeight: '120px',   // cap height on desktop
              minHeight: '90px',
            }}
          >
            <CardSVG card={card} />
          </div>
        ))}
      </div>

      {/* Buttons container */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 w-full max-w-4xl">
        <button
          onClick={() => handleAnswer(true)}
          className="w-full py-2 sm:py-3 bg-green-600 text-white rounded-lg font-semibold text-sm sm:text-base hover:bg-green-700 transition"
        >
          ✅ It's a Set
        </button>
        <button
          onClick={() => handleAnswer(false)}
          className="w-full py-2 sm:py-3 bg-red-600 text-white rounded-lg font-semibold text-sm sm:text-base hover:bg-red-700 transition"
        >
          ❌ Not a Set
        </button>
        <button
          onClick={() => {
            if (pendingTimeout) {
              clearTimeout(pendingTimeout);
              setPendingTimeout(null);
            }
            setGameState('welcome');
            setFeedback(null);
          }}
          className="w-full py-2 sm:py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold text-sm sm:text-base hover:bg-gray-400 transition sm:col-span-2"
        >
          Exit to Menu
        </button>
      </div>
    </div>
  );

};
